import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DeleteOutlined, MenuOutlined } from "@ant-design/icons";
import { DndContext, PointerSensor, useSensor } from "@dnd-kit/core";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Button, Form, Popconfirm, Table } from "antd";
import { EUserRole } from "@devowl-wp/api-real-cookie-banner";
import { QuestionSelection } from "./question-selection.js";
import { useTranslation } from "../../../../../../../others/i18n.js";
import { useStores } from "../../../../../../../store/root.js";
import { DraggableRow } from "../../../../../../third-party/dnd/draggable-row.js";
import { SortableList } from "../../../../../../third-party/dnd/sortable-list.js";
import { PermissionCheck } from "../../../../user/permission.js";
/**
 * Sortable table for purposes with drag and drop support for reordering.
 */ const PurposeTable = ({ columns, formName, purposeOptions = [], ...restProps })=>{
    const { t } = useTranslation();
    const { rcbAuthenticationStore } = useStores();
    const pointerSensor = useSensor(PointerSensor);
    const hasCreatorRole = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    return /*#__PURE__*/ _jsx(Form.List, {
        name: formName,
        rules: [
            {
                validator (_, values) {
                    if (!hasCreatorRole) {
                        return Promise.resolve();
                    }
                    const isEmpty = !values || !values.length;
                    const hasAnswer = values?.some((purpose)=>purpose.answer);
                    if (isEmpty) {
                        return Promise.reject(new Error(t("Please define a minimum of one purpose/answer pair.")));
                    } else if (!hasAnswer) {
                        return Promise.reject(new Error(t("Please give a mimimum of one answer.")));
                    } else {
                        return Promise.resolve();
                    }
                }
            }
        ],
        children: (fields, { add, remove, move }, { errors })=>{
            const tableColumns = [
                {
                    title: t("Sort"),
                    dataIndex: "sort",
                    width: "30px",
                    render: ()=>/*#__PURE__*/ _jsx(MenuOutlined, {
                            style: {
                                cursor: "grab",
                                color: "#999"
                            }
                        })
                },
                ...columns
            ];
            if (hasCreatorRole) {
                tableColumns.push({
                    title: t("Action"),
                    dataIndex: "actions",
                    render: (_, record, index)=>{
                        return /*#__PURE__*/ _jsx(Popconfirm, {
                            placement: "leftTop",
                            title: t("Are you sure to delete this entry?"),
                            onConfirm: ()=>remove(index),
                            okText: t("Yes"),
                            cancelText: t("No"),
                            children: /*#__PURE__*/ _jsx(Button, {
                                icon: /*#__PURE__*/ _jsx(DeleteOutlined, {}),
                                onDoubleClick: ()=>remove(index)
                            })
                        });
                    }
                });
            }
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(PermissionCheck, {
                        minRole: EUserRole.Creator,
                        children: purposeOptions.length > 0 && /*#__PURE__*/ _jsx(QuestionSelection, {
                            add: add,
                            purposeOptions: purposeOptions
                        })
                    }),
                    /*#__PURE__*/ _jsx(Form.ErrorList, {
                        errors: errors
                    }),
                    /*#__PURE__*/ _jsx(DndContext, {
                        sensors: [
                            pointerSensor
                        ],
                        onDragEnd: (event)=>{
                            const from = fields.findIndex((field)=>field.key === +event.active.id);
                            const to = fields.findIndex((field)=>field.key === +event.over?.id);
                            move(from, to);
                        },
                        children: /*#__PURE__*/ _jsx(SortableList, {
                            fields: fields,
                            options: {
                                disabled: !hasCreatorRole,
                                strategy: verticalListSortingStrategy
                            },
                            children: /*#__PURE__*/ _jsx(Table, {
                                columns: tableColumns,
                                dataSource: fields,
                                components: {
                                    body: {
                                        row: DraggableRow
                                    }
                                },
                                pagination: false,
                                rowKey: "key",
                                ...restProps
                            })
                        })
                    })
                ]
            });
        }
    });
};
export { PurposeTable };
